import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import logo from 'assets/img/brand/logo.png'
/*
 * Deep imports, not the kit's index. This is also the /admin route's Suspense fallback (Routes.jsx),
 * which renders before the dashboard's code has loaded, so it ships in the main bundle that every
 * page loads, public ones included. The index would bring the whole kit with it.
 */
import Skeleton from 'components/ui/Skeleton'
import { pageFrame } from 'components/ui/Page'
import {
  BELOW_SIDEBAR_BREAKPOINT,
  MOBILE_TOPBAR_HEIGHT,
  SIDEBAR_COLLAPSED,
  SIDEBAR_EXPANDED_WIDTH,
  SIDEBAR_RAIL_WIDTH,
  hidesSidebar,
  readStoredSidebarMode,
} from './sidebarLayout'

/**
 * The dashboard's frame in outline: the sidebar (or the phone top bar) beside the page area.
 *
 * Drawn twice on a refresh, with nothing moving between the two: first as the Suspense fallback
 * while the dashboard's code loads, with only a title in the page area, then by AdminShellSkeleton
 * while the photographer record loads, with the outline of the screen being opened. Both used to be
 * a spinner alone on a white page.
 *
 * Nothing in it depends on the account: the plan decides which sidebar items show and which are
 * locked, so they are drawn as bars rather than guessed. `narrow` and `railed` default to what
 * Admin.jsx will decide, read once, since the dashboard's own hooks are not loaded yet.
 */
const NAV_GROUPS = [2, 6, 6]

/* The screens on the subtle surface rather than white, so the page area does not change colour
   under the outline when they take over from it. */
const SUBTLE_PAGES = ['/admin/portfolio', '/admin/galleries', '/admin/gallery/edit/']

const narrowNow = () =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(BELOW_SIDEBAR_BREAKPOINT).matches

const AdminLoadingFrame = ({ pathname = '', narrow, railed, bare = false, children }) => {
  const isNarrow = narrow ?? narrowNow()
  const subtle = SUBTLE_PAGES.some((page) => pathname.startsWith(page))
  /* Admin.jsx starts the sidebar collapsed on the Packages grid, as well as when it was left so. */
  const isRailed =
    railed ??
    (!isNarrow && (readStoredSidebarMode() === SIDEBAR_COLLAPSED || pathname === '/admin/portfolio'))
  const sidebarless = hidesSidebar(pathname)

  /* The page area: what the caller draws, or a title. A screen without the sidebar (an editor or a
     record page) gets a blank page instead, since its own outline is a different shape. */
  const content =
    children !== undefined ? (
      children
    ) : sidebarless ? null : (
      <TitleBlock>
        <Skeleton width={160} height={32} />
      </TitleBlock>
    )

  return (
    <Shell $narrow={isNarrow} aria-busy="true" aria-label="Loading your dashboard">
      {!sidebarless &&
        (isNarrow ? (
          <TopBar aria-hidden="true">
            <IconSpot>
              <Skeleton width={22} height={22} />
            </IconSpot>
            <Logo src={logo} alt="" />
          </TopBar>
        ) : (
          <SideRail $width={isRailed ? SIDEBAR_RAIL_WIDTH : SIDEBAR_EXPANDED_WIDTH} aria-hidden="true">
            <SideHeader $railed={isRailed}>
              {!isRailed && <Logo src={logo} alt="" />}
              <IconSpot>
                <Skeleton width={22} height={22} />
              </IconSpot>
            </SideHeader>
            <SideNav>
              {NAV_GROUPS.map((count, group) => (
                <SideGroup key={group}>
                  {!isRailed && (
                    <GroupBar>
                      <Skeleton width={64} height={10} />
                    </GroupBar>
                  )}
                  {Array.from({ length: count }, (_, item) => (
                    <SideItem key={item} $railed={isRailed}>
                      <Skeleton width={20} height={20} borderRadius={6} />
                      {/* Varied, so the column reads as labels rather than as a block. */}
                      {!isRailed && <Skeleton width={56 + ((group * 29 + item * 13) % 64)} height={12} />}
                    </SideItem>
                  ))}
                </SideGroup>
              ))}
            </SideNav>
            <SideFooter $railed={isRailed}>
              <Skeleton circle width={32} height={32} />
              {!isRailed && <Skeleton width={120} height={12} />}
            </SideFooter>
          </SideRail>
        ))}
      {/* `bare`: a screen's own loading view, which brings its own page frame. */}
      <Main aria-hidden="true" $subtle={subtle}>
        {content && (bare ? content : <Frame>{content}</Frame>)}
      </Main>
    </Shell>
  )
}

AdminLoadingFrame.propTypes = {
  /** The route being opened. */
  pathname: PropTypes.string,
  /** Below the sidebar breakpoint, where the sidebar is a drawer behind a top bar. */
  narrow: PropTypes.bool,
  /** The sidebar collapsed to its icon rail. */
  railed: PropTypes.bool,
  /** Draw `children` as they are, without the page frame, for a screen's own loading view. */
  bare: PropTypes.bool,
  /** The page area's outline. Leave it out for a title alone; `null` draws a blank page. */
  children: PropTypes.node,
}

export default AdminLoadingFrame

/* The title and the rule under it, which every screen with the sidebar opens with. */
export const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin-bottom: var(--space-6);
  padding-bottom: var(--space-6);
  border-bottom: 1px solid var(--ui-border);
  line-height: 0;
`

/* The sidebar's measurements, from Admin.jsx, AdminTab and AdminNavbar, so the real one lands on
   top of this without moving. */
const Shell = styled.div`
  display: flex;
  flex-direction: ${({ $narrow }) => ($narrow ? 'column' : 'row')};
  width: 100%;
  min-height: 100vh;
  background: var(--ui-surface);
`

const SideRail = styled.div`
  display: flex;
  flex: 0 0 ${({ $width }) => $width}px;
  flex-direction: column;
  width: ${({ $width }) => $width}px;
  height: 100vh;
  height: 100dvh;
  box-sizing: border-box;
  background: var(--ui-surface);
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.05);
`

const SideHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $railed }) => ($railed ? 'center' : 'space-between')};
  gap: 8px;
  padding: 12px 8px 12px ${({ $railed }) => ($railed ? '8px' : '16px')};
`

const Logo = styled.img`
  display: block;
  width: auto;
  height: 26px;
`

const IconSpot = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  line-height: 0;
`

const SideNav = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  padding: 4px 8px 12px;
`

const SideGroup = styled.div`
  & + & {
    margin-top: 18px;
  }
`

const GroupBar = styled.div`
  padding: 3px 12px 9px;
  line-height: 0;
`

const SideItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $railed }) => ($railed ? 'center' : 'flex-start')};
  gap: 14px;
  min-height: 44px;
  padding: ${({ $railed }) => ($railed ? '0' : '0 12px')};
  line-height: 0;

  & + & {
    margin-top: 2px;
  }
`

const SideFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $railed }) => ($railed ? 'center' : 'flex-start')};
  gap: 10px;
  min-height: 48px;
  padding: 14px 16px;
  border-top: 1px solid var(--ui-border);
  line-height: 0;
`

const TopBar = styled.div`
  display: flex;
  flex: 0 0 ${MOBILE_TOPBAR_HEIGHT}px;
  align-items: center;
  gap: 8px;
  height: ${MOBILE_TOPBAR_HEIGHT}px;
  padding: 0 8px;
  box-sizing: border-box;
  border-bottom: 1px solid var(--ui-border);
`

const Main = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  background: ${({ $subtle }) => ($subtle ? 'var(--ui-surface-subtle)' : 'transparent')};
`

/* The admin page frame, at the width most screens cap at. */
const Frame = styled.div`
  ${pageFrame}
  box-sizing: border-box;
  max-width: 1100px;
`
